/**
 * «Табель» — табель прихода, таблица часов и цеха листа (§12.7 ТЗ `ops`, D65,
 * D75, D214).
 *
 * **Табель прихода** повторяет бумажный табель ресторана, но начинается пустым
 * (просьба владельца 25.09.2026): заголовок цеха и строка «+», из которой
 * менеджер выбирает человека из своего цеха и ставит ему приход и уход.
 * Заполняют по ходу дня («сейчас») или разом вечером; открывается кнопкой
 * из операционного бота прямо в Telegram. Прошлые дни закрыты, пока
 * управляющий или собственник не открыл их тумблером.
 *
 * **Таблица** — как таблица ресторана для зарплаты: люди × дни, в клетке
 * приход и уход, обычные часы и завтрак. Копируется целиком и скачивается
 * файлом Excel или CSV. Смена в выгрузке — **числом**, как у ресторана
 * (09:20–23:20 → 9202320): строку человека вставляют в его строку таблицы
 * для зарплаты, и её формулы считают часы сами. Своё право — «Табель ·
 * таблица часов»: без него вкладки нет.
 *
 * Часы считает сервер по правилам ресторана — здесь только показ.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { Icon } from '../../icons';
import { navigate, useRoute } from '../../router';
import { Notice, isoDate } from '../../ui';
import { LIVE, shiftApi } from './api';
import type {
  BreakfastMode,
  DaySheet,
  FreeMember,
  GridRow,
  OnCallAnswer,
  HoursGrid,
  PositionSetup,
  PositionsSetup,
  SheetEntry,
  SheetInput,
  ShiftAccess,
} from './api';
import { currentPlace } from './location';
import { shiftDay } from './shared';
import { TimeRangeField } from './TimeRangeField';

type View = 'sheet' | 'table' | 'setup';

const VIEWS: { code: View; title: string }[] = [
  { code: 'sheet', title: 'Табель прихода' },
  { code: 'table', title: 'Таблица' },
  { code: 'setup', title: 'Цеха и завтраки' },
];

const BREAKFAST_TITLES: Record<BreakfastMode, string> = {
  none: 'нет',
  hours: 'часами (зал)',
  unit: 'единицей (кухня)',
};

function viewPath(view: View, extra: Record<string, string | undefined> = {}): string {
  const search = new URLSearchParams();
  if (view !== 'sheet') search.set('view', view);
  for (const [key, value] of Object.entries(extra)) if (value) search.set(key, value);
  const text = search.toString();
  return `/shift/timesheet${text ? `?${text}` : ''}`;
}

export function Timesheet({ access }: { access: ShiftAccess }) {
  const route = useRoute();
  // Каждая вкладка — своя галочка (D235): лист — «Табель», таблица —
  // «Табель · таблица часов», цеха — «Табель · цеха и завтраки».
  const views = VIEWS.filter(
    (item) =>
      (item.code !== 'table' || access.timesheet_table) &&
      (item.code !== 'setup' || access.timesheet_setup),
  );
  const asked = route.params.get('view');
  const view: View = views.some((item) => item.code === asked) ? (asked as View) : 'sheet';

  return (
    <div className="shift-stack">
      <div className="chips" role="tablist" aria-label="Табель">
        {views.map((item) => (
          <button
            key={item.code}
            type="button"
            role="tab"
            aria-selected={item.code === view}
            className={`chip${item.code === view ? ' chip--active' : ''}`}
            onClick={() => navigate(viewPath(item.code), { replace: true })}
          >
            {item.title}
          </button>
        ))}
      </div>
      {view === 'sheet' ? (
        <SheetView date={route.params.get('date') ?? undefined} />
      ) : view === 'table' ? (
        <TableView from={route.params.get('from') ?? undefined} />
      ) : (
        <SetupView />
      )}
    </div>
  );
}

/** Запись строки листа; при проверке места — с местом телефона (D214). */
async function saveWithPlace(
  sheet: DaySheet,
  employeeId: number,
  input: SheetInput,
): Promise<unknown> {
  const location = sheet.geo_check ? await currentPlace() : null;
  return shiftApi.saveSheet(sheet.date, employeeId, location ? { ...input, location } : input);
}

// ── табель прихода ─────────────────────────────────────────────────────────────

const sheetKey = (date?: string) => ['shift', 'timesheet', 'sheet', date ?? 'today'] as const;

function SheetView({ date }: { date?: string }) {
  const client = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const query = useQuery<DaySheet, ApiError>({
    queryKey: sheetKey(date),
    queryFn: () => shiftApi.sheet(date),
    ...LIVE,
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['shift', 'timesheet'] });
  const backfill = useMutation<{ message: string }, ApiError, boolean>({
    mutationFn: (open) => shiftApi.backfill(open),
    onSuccess: (answer) => setResult({ ok: true, text: answer.message }),
    onError: (error) => setResult({ ok: false, text: error.detail ?? 'Не получилось.' }),
    onSettled: refresh,
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const sheet = query.data;
  const current = sheet.date;
  const go = (day: string | undefined) =>
    navigate(viewPath('sheet', { date: day && day !== sheet.today ? day : undefined }), {
      replace: true,
    });
  const entries = sheet.sections.flatMap((section) => section.entries);
  const filled = entries.filter((entry) => entry.started).length;
  const open = entries.filter((entry) => entry.open).length;

  return (
    <>
      <div className="shift-dayline">
        <button
          type="button"
          className="button button--small"
          aria-label="Предыдущий день"
          onClick={() => go(shiftDay(current, -1))}
        >
          <Icon name="back" size={14} />
        </button>
        <input
          className="input shift-date"
          type="date"
          aria-label="День"
          value={current}
          max={sheet.today}
          onChange={(event) => go(event.target.value || undefined)}
        />
        <button
          type="button"
          className="button button--small shift-next"
          aria-label="Следующий день"
          disabled={current >= sheet.today}
          onClick={() => go(shiftDay(current, 1))}
        >
          <Icon name="back" size={14} />
        </button>
        {current !== sheet.today && (
          <button type="button" className="button button--small" onClick={() => go(undefined)}>
            Сегодня
          </button>
        )}
      </div>

      {sheet.can_switch_backfill && (
        <label className="ts-switch">
          <input
            type="checkbox"
            checked={sheet.backfill_open}
            disabled={backfill.isPending}
            onChange={(event) => {
              setResult(null);
              backfill.mutate(event.target.checked);
            }}
          />
          <span>
            <strong>Прошлые дни</strong>
            <span className="faint">
              {sheet.backfill_open
                ? ' — открыты: лист можно заполнять задним числом. Закончили — выключите.'
                : ' — закрыты: заполняется только сегодняшний лист.'}
            </span>
          </span>
        </label>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      {sheet.is_keeper && !sheet.can_edit && current < sheet.today && (
        <Notice kind="error">
          Прошлые дни закрыты для правки.{' '}
          {sheet.can_switch_backfill
            ? 'Включите «Прошлые дни» выше.'
            : 'Открыть их может управляющий или собственник.'}
        </Notice>
      )}

      {sheet.geo_check && sheet.can_edit && (
        <p className="shift-hint">
          📍 Сегодняшний лист заполняют в ресторане: с каждой записью телефон сообщает, где
          он. Записанное не на месте сохранится с пометкой, и владелец об этом узнает.
        </p>
      )}
      <section className="shift-card ts-sheet" aria-label="Табель прихода">
        <header className="shift-card__head">
          <h2 className="shift-card__title">График прихода и ухода · {isoDate(current)}</h2>
          <span className="faint">
            отмечено: {filled}
            {open ? ` · не ушли: ${open}` : ''}
          </span>
        </header>
        {sheet.sections.length === 0 ? (
          <p className="faint">
            В листе нет ни одного цеха. Поставьте должности в цеха на вкладке «Цеха и завтраки».
          </p>
        ) : (
          sheet.sections.map((section) => (
            <div key={section.title} className="ts-section">
              <h3 className="ts-section__title">{section.title}</h3>
              <ul className="ts-rows">
                {section.entries.map((entry) => (
                  <EntryRow
                    key={`${entry.employee_id}-${entry.position_id ?? 0}`}
                    entry={entry}
                    sheet={sheet}
                    onDone={(ok, text) => {
                      setResult(ok ? null : { ok, text });
                      refresh();
                    }}
                  />
                ))}
                {sheet.can_edit && (
                  <PlusRow section={section} sheet={sheet} onDone={refresh} />
                )}
                {!sheet.can_edit && section.entries.length === 0 && (
                  <li className="faint ts-empty">никого</li>
                )}
              </ul>
            </div>
          ))
        )}
      </section>
      <p className="shift-hint">
        Часы — с одним знаком после запятой. Завтрак — часть смены с 9:30 до 11:00: у зала
        пишется часами, у кухни единицей, и вычитается из обычных часов. Уход после полуночи —
        та же смена.
      </p>
    </>
  );
}

function EntryRow({
  entry,
  sheet,
  onDone,
}: {
  entry: SheetEntry;
  sheet: DaySheet;
  onDone: (ok: boolean, text: string) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const isToday = sheet.date === sheet.today;
  const moved = entry.position_id !== entry.home_position_id;
  const save = useMutation<unknown, ApiError, SheetInput>({
    mutationFn: (input) =>
      saveWithPlace(sheet, entry.employee_id, {
        ...input,
        position_id: moved ? entry.position_id : null,
      }),
    onSuccess: () => {
      setError(null);
      onDone(true, '');
    },
    onError: (failure) => setError(failure.detail ?? 'Не получилось сохранить.'),
  });
  const remove = useMutation<unknown, ApiError, void>({
    mutationFn: () => shiftApi.removeSheet(sheet.date, entry.employee_id),
    onSuccess: () => onDone(true, ''),
    onError: (failure) => setError(failure.detail ?? 'Не получилось убрать.'),
  });

  const commit = (nextStarted: string, nextFinished: string) => {
    if (!sheet.can_edit) return;
    if (nextStarted === entry.started && nextFinished === (entry.finished ?? '')) return;
    save.mutate({ started: nextStarted, finished: nextFinished || null });
  };
  const busy = save.isPending || remove.isPending;

  return (
    <li className={`ts-row${entry.open ? ' ts-row--open' : ''}`}>
      <div className="ts-row__who">
        <span className="ts-row__name">
          {entry.name}
          {entry.on_call && <span className="ts-oncall">вызывной</span>}
        </span>
        {moved && entry.position_name && <span className="faint">сегодня: {entry.position_name}</span>}
        {entry.outside && (
          <span className="ts-outside" title="Записано не в ресторане">
            📍 не в ресторане: {entry.outside}
          </span>
        )}
      </div>
      <div className="ts-row__times">
        <div className="ts-time">
          <span aria-hidden="true">приход – уход</span>
          <TimeRangeField
            started={entry.started}
            finished={entry.finished ?? ''}
            disabled={!sheet.can_edit || busy}
            onCommit={commit}
            onEmpty={() => setError('Без прихода строки нет — уберите её крестиком.')}
          />
        </div>
        <div className="ts-row__hours" aria-label="Часы">
          {entry.regular ? (
            <>
              <strong className="num">{entry.regular}</strong>
              {entry.breakfast && (
                <span className="faint">
                  завтрак {entry.breakfast_mode === 'unit' ? '✓' : entry.breakfast}
                </span>
              )}
            </>
          ) : entry.open ? (
            <span className="faint">на смене</span>
          ) : null}
        </div>
      </div>
      {sheet.can_edit && (
        <div className="ts-row__actions">
          {isToday && entry.open && (
            <button
              type="button"
              className="button button--small"
              disabled={busy}
              onClick={() => save.mutate({ started: entry.started, finished: 'now' })}
            >
              Ушёл сейчас
            </button>
          )}
          {(
            <button
              type="button"
              className="button button--small ts-remove"
              disabled={busy}
              aria-label={`Убрать ${entry.name} из листа`}
              onClick={() => {
                if (window.confirm(`Убрать отметку «${entry.name}» за этот день?`)) remove.mutate();
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}
      {error && <p className="ts-row__error">{error}</p>}
    </li>
  );
}

/**
 * Строка «+» под цехом: выбрать человека и поставить ему приход и уход.
 *
 * Сначала в списке «свои» — люди должностей этого цеха; ниже — люди
 * других цехов **того же подразделения** (зал или кухня): повар подменяет
 * повара, официант — бармена, но официант на кухню не встаёт (уточнение
 * владельца 25.09.2026). Подменяющий записывается в должности этого цеха —
 * по ней считается завтрак.
 */
function PlusRow({
  section,
  sheet,
  onDone,
}: {
  section: DaySheet['sections'][number];
  sheet: DaySheet;
  onDone: () => void;
}) {
  const [picked, setPicked] = useState<FreeMember | null>(null);
  const [manual, setManual] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const regular = sheet.free.filter((member) => !member.on_call);
  const own = regular.filter(
    (member) => member.position_id !== null && section.position_ids.includes(member.position_id),
  );
  const rest = regular.filter(
    (member) =>
      !own.includes(member) &&
      member.position_id !== null &&
      section.department_position_ids.includes(member.position_id),
  );
  // Вызывные (D266) — своей группой внизу: свои и того же подразделения.
  const onCall = sheet.free.filter(
    (member) =>
      member.on_call &&
      member.position_id !== null &&
      (section.position_ids.includes(member.position_id) ||
        section.department_position_ids.includes(member.position_id)),
  );
  const isToday = sheet.date === sheet.today;

  const reset = () => {
    setPicked(null);
    setError(null);
  };
  const save = useMutation<unknown, ApiError, SheetInput>({
    mutationFn: (input) => {
      const fromHere =
        picked!.position_id !== null && section.position_ids.includes(picked!.position_id);
      return saveWithPlace(sheet, picked!.employee_id, {
        ...input,
        position_id: fromHere ? null : (section.position_ids[0] ?? null),
      });
    },
    onSuccess: () => {
      reset();
      onDone();
    },
    onError: (failure) => setError(failure.detail ?? 'Не получилось сохранить.'),
  });
  const commit = (nextStarted: string, nextFinished: string) => {
    if (nextStarted) save.mutate({ started: nextStarted, finished: nextFinished || null });
  };

  if (manual) {
    return (
      <ManualRow
        section={section}
        sheet={sheet}
        onDone={() => {
          setManual(false);
          onDone();
        }}
        onCancel={() => setManual(false)}
      />
    );
  }

  if (!picked) {
    return (
      <li className="ts-plus">
        <span className="ts-plus__icon" aria-hidden="true">
          +
        </span>
        <select
          className="input ts-plus__select"
          aria-label={`Добавить в «${section.title}»`}
          value=""
          onChange={(event) => {
            if (event.target.value === MANUAL) {
              setManual(true);
              return;
            }
            const member = sheet.free.find((m) => String(m.employee_id) === event.target.value);
            if (member) setPicked(member);
          }}
        >
          <option value="">Добавить…</option>
          {own.length + rest.length + onCall.length === 0 && (
            <option value="" disabled>
              в цехе и подразделении все уже отмечены
            </option>
          )}
          {own.length > 0 && (
            <optgroup label={section.title}>
              {own.map((member) => (
                <option key={member.employee_id} value={member.employee_id}>
                  {member.name}
                </option>
              ))}
            </optgroup>
          )}
          {rest.length > 0 && (
            <optgroup label={`${section.department ?? 'Подразделение'} · другие цеха`}>
              {rest.map((member) => (
                <option key={member.employee_id} value={member.employee_id}>
                  {member.name}
                </option>
              ))}
            </optgroup>
          )}
          {onCall.length > 0 && (
            <optgroup label="Вызывные">
              {onCall.map((member) => (
                <option key={member.employee_id} value={member.employee_id}>
                  {member.name}
                </option>
              ))}
            </optgroup>
          )}
          <option value={MANUAL}>✎ Вписать вручную…</option>
        </select>
      </li>
    );
  }

  const busy = save.isPending;
  return (
    <li className="ts-row ts-row--new">
      <div className="ts-row__who">
        <span className="ts-row__name">{picked.name}</span>
        <span className="faint">наберите время цифрами — строка сохранится</span>
      </div>
      <div className="ts-row__times">
        <div className="ts-time">
          <span aria-hidden="true">приход – уход</span>
          <TimeRangeField started="" finished="" autoFocus disabled={busy} onCommit={commit} />
        </div>
      </div>
      <div className="ts-row__actions">
        {isToday && (
          <button
            type="button"
            className="button button--small"
            disabled={busy}
            onClick={() => save.mutate({ started: 'now', finished: null })}
          >
            Пришёл сейчас
          </button>
        )}
        {picked.last && (
          <button
            type="button"
            className="button button--small"
            disabled={busy}
            title="Время последней смены"
            onClick={() =>
              save.mutate({ started: picked.last!.started, finished: picked.last!.finished })
            }
          >
            как в прошлый раз {picked.last.started}–{picked.last.finished}
          </button>
        )}
        <button
          type="button"
          className="button button--small ts-remove"
          disabled={busy}
          aria-label="Не добавлять"
          onClick={reset}
        >
          ✕
        </button>
      </div>
      {error && <p className="ts-row__error">{error}</p>}
    </li>
  );
}

/** Пункт строки «+» — вписать человека, которого нет в списке (D266). */
const MANUAL = 'manual';

type Times = { started: string; finished: string | null };
type Match = Extract<OnCallAnswer, { status: 'same_name' }>['matches'][number];

/**
 * Вызывной, вписанный вручную (D266): имя и фамилия, должность цеха, время.
 *
 * Человека заводит платформа — строка табеля обязана ссылаться на
 * сотрудника, иначе его часов нет в таблице для зарплаты. Запись идёт,
 * когда есть и имя, и время, в каком бы порядке их ни заполнили. Тёзка не
 * заводится молча: «Иванова Алина (официант) уже есть в системе — это она?».
 */
function ManualRow({
  section,
  sheet,
  onDone,
  onCancel,
}: {
  section: DaySheet['sections'][number];
  sheet: DaySheet;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [positionId, setPositionId] = useState<number | null>(section.position_ids[0] ?? null);
  const [times, setTimes] = useState<Times | null>(null);
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isToday = sheet.date === sheet.today;
  const positions = sheet.positions.filter((item) => section.position_ids.includes(item.position_id));
  const clean = name.trim().replace(/\s+/g, ' ');

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError((failure as ApiError).detail ?? 'Не получилось сохранить.');
    } finally {
      setBusy(false);
    }
  };
  const mark = async (employeeId: number, home: number | null, next: Times) => {
    const fromHere = home !== null && section.position_ids.includes(home);
    await saveWithPlace(sheet, employeeId, {
      ...next,
      position_id: fromHere ? null : positionId,
    });
    onDone();
  };
  const create = async (next: Times, otherPerson: boolean) => {
    const answer = await shiftApi.addOnCall(clean, positionId!, otherPerson);
    if (answer.status === 'same_name') {
      setMatches(answer.matches);
      return;
    }
    await mark(answer.employee_id, positionId, next);
  };
  const attempt = (next: Times | null) => {
    if (!next || busy || matches) return;
    if (clean.split(' ').length < 2) {
      setError('Напишите имя и фамилию — тогда строка сохранится.');
      return;
    }
    if (positionId === null) {
      setError('У цеха нет должности — поставьте её на вкладке «Цеха и завтраки».');
      return;
    }
    void run(() => create(next, false));
  };
  const choose = (match: Match) => {
    const member = sheet.free.find((item) => item.employee_id === match.employee_id);
    if (!member) {
      setError(`«${match.full_name}» уже отмечен(а) сегодня — строка выше в листе.`);
      return;
    }
    void run(() => mark(member.employee_id, member.position_id, times!));
  };
  const asked = (match: Match) =>
    `${match.full_name}${match.position_name ? ` (${match.position_name.toLowerCase()})` : ''}`;

  return (
    <li className="ts-row ts-row--new">
      <div className="ts-row__who ts-manual">
        <input
          className="input ts-manual__name"
          aria-label="Имя и фамилия"
          placeholder="Имя и фамилия"
          autoFocus
          autoComplete="off"
          value={name}
          disabled={busy || matches !== null}
          onChange={(event) => {
            setName(event.target.value);
            setError(null);
          }}
          onBlur={() => attempt(times)}
        />
        {positions.length > 1 ? (
          <select
            className="input ts-manual__position"
            aria-label="Должность"
            value={positionId ?? ''}
            disabled={busy || matches !== null}
            onChange={(event) => setPositionId(Number(event.target.value))}
          >
            {positions.map((item) => (
              <option key={item.position_id} value={item.position_id}>
                {item.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="faint">вызывной · {positions[0]?.name ?? section.title}</span>
        )}
      </div>
      <div className="ts-row__times">
        <div className="ts-time">
          <span aria-hidden="true">приход – уход</span>
          <TimeRangeField
            started={times && times.started !== 'now' ? times.started : ''}
            finished={times?.finished ?? ''}
            disabled={busy || matches !== null}
            onCommit={(started, finished) => {
              const next = { started, finished: finished || null };
              setTimes(next);
              attempt(next);
            }}
          />
        </div>
      </div>
      {matches ? (
        <div className="ts-manual__ask" role="group" aria-label="Такой человек уже есть">
          {matches.map((match) => (
            <p key={match.employee_id}>
              <strong>{asked(match)}</strong> уже есть в системе — это{' '}
              {matches.length > 1 ? 'кто-то из них' : 'он(а)'}?
            </p>
          ))}
          <div className="ts-row__actions">
            {matches.map((match) => (
              <button
                key={match.employee_id}
                type="button"
                className="button button--small button--primary"
                disabled={busy}
                onClick={() => choose(match)}
              >
                {matches.length > 1 ? `Да: ${asked(match)}` : 'Да, это он(а)'}
              </button>
            ))}
            <button
              type="button"
              className="button button--small"
              disabled={busy}
              onClick={() => void run(() => create(times!, true))}
            >
              Нет, другой человек
            </button>
          </div>
        </div>
      ) : (
        <div className="ts-row__actions">
          {isToday && (
            <button
              type="button"
              className="button button--small"
              disabled={busy}
              onClick={() => {
                const next = { started: 'now', finished: null };
                setTimes(next);
                attempt(next);
              }}
            >
              Пришёл сейчас
            </button>
          )}
          <button
            type="button"
            className="button button--small ts-remove"
            disabled={busy}
            aria-label="Не вписывать"
            onClick={onCancel}
          >
            ✕
          </button>
        </div>
      )}
      {error && <p className="ts-row__error">{error}</p>}
    </li>
  );
}

// ── таблица ──────────────────────────────────────────────────────────────

/** Половина месяца, в которую попадает день: 1–15 или 16–конец. */
function halfOf(iso: string): { from: string; to: string } {
  const [y, m, d] = iso.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (d <= 15) return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-15` };
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${y}-${pad(m)}-16`, to: `${y}-${pad(m)}-${pad(last)}` };
}

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

function dayTitle(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}, ${weekday}`;
}

/**
 * Таблица строками — для копирования и CSV. Раскладка как у таблицы
 * ресторана, смена — числом её формата: вставленная туда строка считается
 * формулами ресторана. Смена без ухода остаётся текстом — числа у неё нет.
 */
function shiftValue(cell: GridRow['cells'][string] | undefined): string {
  if (!cell) return '';
  return cell.payroll_number === null ? `${cell.started} - …` : String(cell.payroll_number);
}

/**
 * Смены одного человека за период — одной строкой, только дни. Её вставляют
 * в строку смен этого человека в таблице для зарплаты, с клетки первого дня.
 */
export function rowLine(grid: HoursGrid, row: GridRow): string {
  return grid.days.map((day) => shiftValue(row.cells[day])).join('\t');
}

export function gridLines(grid: HoursGrid): string[][] {
  const lines: string[][] = [['ФИО', 'Должность', ...grid.days.map(dayTitle), 'Итого часов']];
  for (const section of grid.sections) {
    lines.push([section.title]);
    for (const row of section.rows) {
      lines.push([
        row.name,
        row.position_name ?? '',
        ...grid.days.map((day) => shiftValue(row.cells[day])),
        '',
      ]);
      lines.push([
        '',
        'часы',
        ...grid.days.map((day) => row.cells[day]?.regular ?? ''),
        row.regular_total,
      ]);
      if (row.breakfast_mode !== 'none') {
        lines.push([
          '',
          'завтрак',
          ...grid.days.map((day) => row.cells[day]?.breakfast ?? ''),
          row.breakfast_total,
        ]);
      }
    }
  }
  return lines;
}

function download(name: string, text: string) {
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function TableView({ from }: { from?: string }) {
  const [copied, setCopied] = useState<string | null>(null);
  const [copiedRow, setCopiedRow] = useState<string | null>(null);
  const query = useQuery<HoursGrid, ApiError>({
    queryKey: ['shift', 'timesheet', 'grid', from ?? 'current'],
    queryFn: () => {
      const half = from ? halfOf(from) : undefined;
      return shiftApi.grid(half?.from, half?.to);
    },
    ...LIVE,
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const grid = query.data;
  const go = (day: string) => navigate(viewPath('table', { from: day }), { replace: true });
  const previous = halfOf(shiftDay(grid.from, -1)).from;
  const next = shiftDay(grid.to, 1);
  const lines = gridLines(grid);

  const copyRow = async (key: string, row: GridRow) => {
    try {
      await navigator.clipboard.writeText(rowLine(grid, row));
      setCopiedRow(key);
      setCopied(
        `Строка «${row.name}» скопирована: ${grid.days.length} дн., с ${dayTitle(grid.from)}. ` +
          'В таблице для зарплаты встаньте на клетку этого дня в строке смен человека ' +
          'и нажмите Ctrl+Shift+V — формат ячеек и формулы останутся.',
      );
    } catch {
      setCopiedRow(null);
      setCopied('Не получилось скопировать: браузер не дал доступ к буферу обмена.');
    }
  };

  const copy = async () => {
    const text = lines.map((line) => line.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRow(null);
      setCopied(
        'Таблица скопирована. Смены — числами, как в таблице для зарплаты: строку человека ' +
          'вставляйте в его строку там через Ctrl+Shift+V, чтобы остался формат ячеек.',
      );
    } catch {
      setCopied('Не получилось скопировать: браузер не дал доступ к буферу обмена.');
    }
  };

  return (
    <>
      <div className="shift-dayline">
        <button
          type="button"
          className="button button--small"
          aria-label="Предыдущие полмесяца"
          onClick={() => go(previous)}
        >
          <Icon name="back" size={14} />
        </button>
        <strong>
          {isoDate(grid.from)} — {isoDate(grid.to)}
        </strong>
        <button
          type="button"
          className="button button--small shift-next"
          aria-label="Следующие полмесяца"
          disabled={next > grid.today}
          onClick={() => go(next)}
        >
          <Icon name="back" size={14} />
        </button>
        <span className="ts-spacer" />
        <button type="button" className="button button--small export-action" onClick={copy}>
          Скопировать таблицу
        </button>
        <a
          className="button button--small export-action"
          href={shiftApi.gridXlsxUrl(grid.from, grid.to)}
          download={`tabel_${grid.from}_${grid.to}.xlsx`}
          title="Смены в формате таблицы для зарплаты: 09:20 - 23:20"
        >
          Скачать Excel
        </a>
        <button
          type="button"
          className="button button--small export-action"
          onClick={() =>
            download(
              `tabel_${grid.from}_${grid.to}.csv`,
              lines.map((line) => line.map(csvCell).join(';')).join('\r\n'),
            )
          }
        >
          Скачать CSV
        </button>
      </div>
      {copied && <Notice kind="ok">{copied}</Notice>}

      <div className="ts-grid-wrap">
        <table className="ts-grid">
          <thead>
            <tr>
              <th className="ts-grid__name">ФИО</th>
              {grid.days.map((day) => (
                <th key={day} className={day === grid.today ? 'ts-grid__today' : undefined}>
                  {dayTitle(day)}
                </th>
              ))}
              <th>Итого</th>
            </tr>
          </thead>
          <tbody>
            {grid.sections.map((section) => (
              <SectionRows
                key={section.title}
                section={section}
                grid={grid}
                copiedRow={copiedRow}
                onCopyRow={copyRow}
              />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th className="ts-grid__name">Всего часов</th>
              <td colSpan={grid.days.length} />
              <td className="num">
                <strong>{grid.regular_total}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {grid.sections.some((s) => s.rows.some((r) => r.open_count)) && (
        <p className="faint">
          «…» — смена без ухода: часы по ней не считаются, пока уход не отмечен.
        </p>
      )}
      {grid.kitchen_rule &&
        grid.sections.some((s) => s.rows.some((r) => Object.values(r.cells).some((c) => c.ruled))) && (
          <p className="faint">
            <span className="ts-grid__ruled">Подчёркнутое время</span> — приход кухни по правилу
            ресторана: до {grid.kitchen_rule.breakfast_until} включительно —{' '}
            {grid.kitchen_rule.breakfast_from} и завтрак, позже и до {grid.kitchen_rule.start} —{' '}
            {grid.kitchen_rule.start} без завтрака. Что отметили на самом деле — «▸ по факту» у
            человека.
          </p>
        )}
      {grid.sections.some((s) => s.rows.some((r) => Object.values(r.cells).some((c) => c.outside))) && (
        <p className="faint">📍 — записано не в ресторане.</p>
      )}
    </>
  );
}

function csvCell(value: string): string {
  return /[;"\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function SectionRows({
  section,
  grid,
  copiedRow,
  onCopyRow,
}: {
  section: HoursGrid['sections'][number];
  grid: HoursGrid;
  copiedRow: string | null;
  onCopyRow: (key: string, row: GridRow) => void;
}) {
  // Строка «по факту» раскрывается у каждого человека отдельно (D219).
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpened((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return (
    <>
      <tr className="ts-grid__section">
        <th colSpan={grid.days.length + 2}>{section.title}</th>
      </tr>
      {section.rows.map((row) => {
        const rows = row.breakfast_mode === 'none' ? 2 : 3;
        const key = `${row.employee_id}-${row.position_id}`;
        const open = opened.has(key);
        const hasShifts = Object.keys(row.cells).length > 0;
        return [
          <tr key={`${key}-t`} className="ts-grid__times">
            <th rowSpan={rows + (open ? 1 : 0)} className="ts-grid__name">
              {row.name}
              {row.on_call && <span className="ts-oncall">вызывной</span>}
              <span className="faint">{row.position_name}</span>
              <button
                type="button"
                className="ts-copy-row"
                aria-label={`Скопировать строку: ${row.name}`}
                title="Смены за полмесяца — для строки человека в таблице для зарплаты"
                onClick={() => onCopyRow(key, row)}
              >
                {copiedRow === key ? '✓ скопировано' : 'скопировать строку'}
              </button>
              {hasShifts && (
                <button
                  type="button"
                  className="ts-copy-row"
                  aria-expanded={open}
                  aria-label={`По факту: ${row.name}`}
                  title="Что внесли в табель на самом деле и сколько это часов"
                  onClick={() => toggle(key)}
                >
                  {open ? '▾ по факту' : '▸ по факту'}
                </button>
              )}
            </th>
            {grid.days.map((day) => {
              const cell = row.cells[day];
              return (
                <td
                  key={day}
                  className={
                    [
                      cell?.outside ? 'ts-grid__outside' : cell && !cell.finished ? 'ts-grid__open' : '',
                      cell?.ruled ? 'ts-grid__ruled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  title={
                    [
                      cell?.outside ? 'Записано не в ресторане' : '',
                      cell?.ruled ? `По правилу кухни; отметили приход ${cell.fact_started}` : '',
                    ]
                      .filter(Boolean)
                      .join('. ') || undefined
                  }
                >
                  {cell ? `${cell.outside ? '📍 ' : ''}${cell.started}–${cell.finished ?? '…'}` : ''}
                </td>
              );
            })}
            <td rowSpan={rows} className="num ts-grid__total">
              <strong>{row.regular_total}</strong>
              {row.breakfast_mode !== 'none' && (
                <span className="faint">завтр. {row.breakfast_total}</span>
              )}
            </td>
          </tr>,
          <tr key={`${key}-h`} className="ts-grid__hours">
            {grid.days.map((day) => (
              <td key={day} className="num">
                {row.cells[day]?.regular ?? ''}
              </td>
            ))}
          </tr>,
          row.breakfast_mode !== 'none' ? (
            <tr key={`${key}-b`} className="ts-grid__breakfast">
              {grid.days.map((day) => (
                <td key={day} className="num">
                  {row.cells[day]?.breakfast ?? ''}
                </td>
              ))}
            </tr>
          ) : null,
          open ? (
            <tr key={`${key}-f`} className="ts-grid__fact">
              {grid.days.map((day) => {
                const cell = row.cells[day];
                return (
                  <td key={day} className={cell?.ruled ? 'ts-grid__ruled' : undefined}>
                    {cell && (
                      <>
                        {cell.fact_started}–{cell.finished ?? '…'}
                        <span>{cell.fact_hours ?? ''}</span>
                      </>
                    )}
                  </td>
                );
              })}
              <td className="num ts-grid__total" title="Все часы по отметкам">
                {row.fact_total}
                <span className="faint">по факту</span>
              </td>
            </tr>
          ) : null,
        ];
      })}
    </>
  );
}

// ── цеха и завтраки ──────────────────────────────────────────────────────

function SetupView() {
  const query = useQuery<PositionsSetup, ApiError>({
    queryKey: ['shift', 'timesheet', 'positions'],
    queryFn: () => shiftApi.positionsSetup(),
  });
  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const setup = query.data;
  return (
    <section className="shift-card" aria-label="Цеха и завтраки">
      <header className="shift-card__head">
        <h2 className="shift-card__title">Должности в листе</h2>
      </header>
      <p className="faint">
        Цех — заголовок на листе, как на бумаге; порядок — чем меньше число, тем выше цех.
        Подразделение — зал или кухня: внутри него цеха подменяют друг друга, и в строке «+»
        ниже своих людей предлагаются люди других цехов того же подразделения. Подразделение
        задаётся у должности в «Команде → Должности». Должность без
        цеха в лист не попадает. Завтрак считается с {setup.breakfast_from} до{' '}
        {setup.breakfast_to}.
      </p>
      <p className="faint">
        «Приход по правилу кухни»: приход до {setup.kitchen_breakfast_until} включительно
        считается {setup.breakfast_from}, позже и до {setup.kitchen_start} — {setup.kitchen_start},
        ещё позже — как отметили. Завтрак при этом — своя настройка должности: у су-шефа приход
        округляется, а завтрак не вычитается.
      </p>
      <ul className="ts-setup">
        {setup.positions.map((position) => (
          <SetupRow key={position.position_id} position={position} canEdit={setup.can_edit} />
        ))}
      </ul>
    </section>
  );
}

function SetupRow({ position, canEdit }: { position: PositionSetup; canEdit: boolean }) {
  const client = useQueryClient();
  const [section, setSection] = useState(position.section ?? '');
  const [order, setOrder] = useState(String(position.section_order));
  const [breakfast, setBreakfast] = useState<BreakfastMode>(position.breakfast);
  const [arrival, setArrival] = useState(position.kitchen_arrival);
  const [state, setState] = useState<{ ok: boolean; text: string } | null>(null);
  const save = useMutation<PositionsSetup, ApiError, void>({
    mutationFn: () =>
      shiftApi.savePositionSetup(position.position_id, {
        section: section.trim() || null,
        section_order: Number(order) || 100,
        breakfast,
        kitchen_arrival: arrival,
      }),
    onSuccess: () => {
      setState({ ok: true, text: 'Сохранено' });
      client.invalidateQueries({ queryKey: ['shift', 'timesheet'] });
    },
    onError: (error) => setState({ ok: false, text: error.detail ?? 'Не получилось.' }),
  });
  const changed =
    section !== (position.section ?? '') ||
    order !== String(position.section_order) ||
    breakfast !== position.breakfast ||
    arrival !== position.kitchen_arrival;

  return (
    <li className="ts-setup__row">
      <strong className="ts-setup__name">{position.name}</strong>
      <label className="shift-field">
        <span>Цех на листе</span>
        <input
          className="input"
          value={section}
          placeholder="не в листе"
          disabled={!canEdit}
          onChange={(e) => setSection(e.target.value)}
        />
      </label>
      <div className="shift-field">
        <span>Подразделение</span>
        <span className="ts-setup__dept">{position.department ?? '—'}</span>
      </div>
      <label className="shift-field ts-setup__order">
        <span>Порядок</span>
        <input
          className="input"
          inputMode="numeric"
          value={order}
          disabled={!canEdit}
          onChange={(e) => setOrder(e.target.value.replace(/\D/g, ''))}
        />
      </label>
      <label className="shift-field">
        <span>Завтрак</span>
        <select
          className="input"
          value={breakfast}
          disabled={!canEdit}
          onChange={(e) => setBreakfast(e.target.value as BreakfastMode)}
        >
          {(Object.keys(BREAKFAST_TITLES) as BreakfastMode[]).map((mode) => (
            <option key={mode} value={mode}>
              {BREAKFAST_TITLES[mode]}
            </option>
          ))}
        </select>
      </label>
      <label className="ts-setup__check">
        <input
          type="checkbox"
          checked={arrival}
          disabled={!canEdit}
          onChange={(e) => setArrival(e.target.checked)}
        />
        <span>Приход по правилу кухни</span>
      </label>
      {canEdit && (
        <button
          type="button"
          className="button button--small"
          disabled={!changed || save.isPending}
          onClick={() => {
            setState(null);
            save.mutate();
          }}
        >
          Сохранить
        </button>
      )}
      {state && <span className={state.ok ? 'faint' : 'ts-row__error'}>{state.text}</span>}
    </li>
  );
}
